import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Crown,
  ExternalLink,
  Loader2,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { organizationsApi } from '@/lib/api';

const ACCOUNTS_URL = 'https://accounts.soliddigital.com';

export function SettingsTeam() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);

  // Get user's primary organization (agency or first one)
  const primaryOrg = user?.organizations?.find((org: any) => org.type === 'AGENCY')
    || user?.organizations?.[0];

  // Fetch organization members (from Accounts API via organizationsApi)
  const { data: members, isLoading } = useQuery({
    queryKey: ['organization-members', primaryOrg?.id],
    queryFn: () => organizationsApi.getMembers(primaryOrg!.id),
    enabled: !!primaryOrg?.id,
  });

  // Fetch available roles (static roles from Accounts)
  const { data: roles } = useQuery({
    queryKey: ['organization-roles', primaryOrg?.id],
    queryFn: () => organizationsApi.getRoles(primaryOrg!.id),
    enabled: !!primaryOrg?.id,
  });

  // Get current user's membership
  const currentUserMembership = members?.find((m: any) => m.user?.id === user?.id);
  const isCurrentUserAdmin =
    currentUserMembership?.role === 'org_owner' || currentUserMembership?.role === 'org_admin';

  // Mutation to update role
  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, roleId }: { memberId: string; roleId: string }) =>
      organizationsApi.updateMemberRole(primaryOrg!.id, memberId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members', primaryOrg?.id] });
      setEditingMemberId(null);
    },
  });

  const handleRoleChange = (memberUserId: string, roleId: string) => {
    updateRoleMutation.mutate({ memberId: memberUserId, roleId });
  };

  if (!primaryOrg) {
    return (
      <Card className="p-6">
        <p className="text-grey-500">No organization found.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-heading-3 text-secondary">Team Members</h2>
            <p className="text-sm text-grey-500 mt-1">
              {primaryOrg.name}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open(ACCOUNTS_URL, '_blank')}
          >
            <ExternalLink className="w-4 h-4" />
            Add Members
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
          </div>
        ) : members && members.length > 0 ? (
          <div className="divide-y divide-grey-100">
            {[...members].sort((a: any, b: any) => {
              const roleOrder: Record<string, number> = { org_owner: 0, org_admin: 1 };
              const aOrder = roleOrder[a.role] ?? 2;
              const bOrder = roleOrder[b.role] ?? 2;
              if (aOrder !== bOrder) return aOrder - bOrder;
              return (a.user?.name || '').localeCompare(b.user?.name || '');
            }).map((member: any) => {
              const isOwner = member.isOwner;
              const isCurrentMember = member.user?.id === user?.id;
              const canEditRole = isCurrentUserAdmin && !isOwner && !isCurrentMember;
              const isEditing = editingMemberId === member.id;

              return (
                <div key={member.id} className="flex items-center gap-4 py-4">
                  <Avatar
                    name={member.user?.name || 'User'}
                    src={member.user?.avatarUrl}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-secondary">{member.user?.name}</p>
                      {isOwner && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-warning/10 text-warning flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          Owner
                        </span>
                      )}
                      {isCurrentMember && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-grey-100 text-grey-500">
                          You
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-grey-500 truncate">{member.user?.email}</p>
                  </div>

                  {isEditing && roles ? (
                    <div className="flex items-center gap-2">
                      <select
                        className="input py-1 px-2 text-sm"
                        defaultValue={member.role}
                        onChange={(e) => handleRoleChange(member.user.id, e.target.value)}
                        disabled={updateRoleMutation.isPending}
                      >
                        {roles.map((role: any) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingMemberId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        'px-2.5 py-1 text-xs font-medium rounded-full',
                        member.role === 'org_admin' || member.role === 'org_owner'
                          ? 'bg-accent/10 text-accent'
                          : 'bg-grey-100 text-grey-600'
                      )}>
                        {roles?.find((r: any) => r.id === member.role)?.name || 'Member'}
                      </span>
                      {canEditRole && (
                        <button
                          onClick={() => setEditingMemberId(member.id)}
                          className="p-1.5 hover:bg-grey-100 rounded transition-colors"
                          title="Change role"
                        >
                          <SettingsIcon className="w-4 h-4 text-grey-400" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-8 text-center text-grey-500">
            No team members found
          </div>
        )}

        {/* Role Info */}
        <div className="mt-6 p-4 bg-grey-50 rounded-lg">
          <h4 className="font-medium text-secondary text-sm mb-2">Role Permissions</h4>
          <ul className="text-sm text-grey-600 space-y-1">
            <li><strong>Owner:</strong> Full access to all offerings and team management</li>
            <li><strong>Admin:</strong> Can be admin for specific offerings (Hub, Analytics, etc.)</li>
            <li><strong>Member:</strong> Standard access to assigned projects within their offerings</li>
          </ul>
          <p className="text-xs text-grey-500 mt-3">
            Ownership and team membership are managed at the <a href={ACCOUNTS_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">account portal</a>.
          </p>
        </div>
      </Card>
    </div>
  );
}
